import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { groupApi } from '../../api/client';
import './GroupListPage.css';

interface Group {
    id: string;
    name: string;
    path: string;
    description?: string;
    visibility: string;
    createdAt: string;
}

export function GroupListPage() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState<Group[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        loadGroups();
    }, []);

    const loadGroups = async () => {
        try {
            setLoading(true);
            // Pass empty string as parentId to fetch only root groups (parentId IS NULL)
            const result = await groupApi.list(1, 20, { parentId: '' });
            setGroups(result.items || []);
        } catch (err: any) {
            setError(err.message || '加载群组失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async (data: {
        name: string;
        path: string;
        description: string;
        visibility: string;
    }) => {
        try {
            const group = await groupApi.create(data);
            setShowCreateModal(false);
            navigate(`/groups/${group.path}`);
        } catch (err: any) {
            setError(err.message || '创建群组失败');
        }
    };

    if (loading) {
        return (
            <div className="group-list-page">
                <div className="loading">加载中...</div>
            </div>
        );
    }

    return (
        <div className="group-list-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>群组</h1>
                    <p className="subtitle">使用群组来组织相关的项目和成员</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path
                            fill="currentColor"
                            d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Zm7.25-3.25v2.5h2.5a.75.75 0 0 1 0 1.5h-2.5v2.5a.75.75 0 0 1-1.5 0v-2.5h-2.5a.75.75 0 0 1 0-1.5h2.5v-2.5a.75.75 0 0 1 1.5 0Z"
                        />
                    </svg>
                    新建群组
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {groups.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">
                        <svg viewBox="0 0 24 24" width="64" height="64">
                            <path
                                fill="currentColor"
                                d="M12 7a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM10 14c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4Zm13-2v-3h-3v2h3v3h2v-3h-2ZM18 7a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-8 9c-2.05 0-4.17.35-5.58.89C3.58 17.35 4 18.25 4 19v1h12v-1c0-.75.42-1.65 1.58-2.11C16.17 16.35 14.05 16 12 16Z"
                            />
                        </svg>
                    </div>
                    <h2>还没有群组</h2>
                    <p>创建一个群组来组织您的项目和团队成员</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        创建第一个群组
                    </button>
                </div>
            ) : (
                <div className="group-grid">
                    {groups.map((group) => (
                        <Link
                            key={group.id}
                            to={`/groups/${group.path}`}
                            className="group-card"
                        >
                            <div className="group-avatar">
                                {group.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="group-info">
                                <h3 className="group-name">{group.name}</h3>
                                <span className="group-path">@{group.path}</span>
                                {group.description && (
                                    <p className="group-description">{group.description}</p>
                                )}
                            </div>
                            <div className="group-meta">
                                <span className={`visibility-badge ${group.visibility}`}>
                                    {group.visibility === 'public'
                                        ? '公开'
                                        : group.visibility === 'internal'
                                            ? '内部'
                                            : '私有'}
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {showCreateModal && (
                <CreateGroupModal
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateGroup}
                />
            )}
        </div>
    );
}

function CreateGroupModal({
    onClose,
    onSubmit,
}: {
    onClose: () => void;
    onSubmit: (data: {
        name: string;
        path: string;
        description: string;
        visibility: string;
    }) => void;
}) {
    const [name, setName] = useState('');
    const [path, setPath] = useState('');
    const [pathEdited, setPathEdited] = useState(false);
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState('private');
    const [submitting, setSubmitting] = useState(false);

    // 自动生成 slug
    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleNameChange = (value: string) => {
        setName(value);
        if (!pathEdited) {
            setPath(generateSlug(value));
        }
    };

    const handlePathChange = (value: string) => {
        setPathEdited(true);
        setPath(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        await onSubmit({ name, path: path || generateSlug(name), description, visibility });
        setSubmitting(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>创建群组</h2>
                    <button className="close-btn" onClick={onClose}>
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <p className="form-description">
                            群组可以用于管理和协作多个项目。群组成员可以访问其中的所有项目。
                        </p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="name">群组名称</label>
                        <input
                            id="name"
                            type="text"
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="例如：我的团队"
                            required
                        />
                        <span className="form-hint">
                            可以使用字母、数字、短横线、下划线
                        </span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="path">群组 URL</label>
                        <div className="input-with-prefix">
                            <span className="input-prefix">gitcenter.com/</span>
                            <input
                                id="path"
                                type="text"
                                value={path}
                                onChange={(e) => handlePathChange(e.target.value)}
                                placeholder="my-team"
                                required
                            />
                        </div>
                        <span className="form-hint">
                            URL 路径只能包含小写字母、数字和短横线
                        </span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">描述（可选）</label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="简单描述这个群组的用途"
                            rows={2}
                        />
                    </div>

                    <div className="form-group">
                        <label>可见性级别</label>
                        <div className="visibility-cards">
                            <label className={`visibility-card ${visibility === 'private' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="private"
                                    checked={visibility === 'private'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M4 4a4 4 0 0 1 8 0v2h.25c.966 0 1.75.784 1.75 1.75v5.5A1.75 1.75 0 0 1 12.25 15h-8.5A1.75 1.75 0 0 1 2 13.25v-5.5C2 6.784 2.784 6 3.75 6H4Zm8.25 3.5h-8.5a.25.25 0 0 0-.25.25v5.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25v-5.5a.25.25 0 0 0-.25-.25ZM10.5 6V4a2.5 2.5 0 1 0-5 0v2Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>私有</strong>
                                    <span>群组及其项目仅对成员可见</span>
                                </div>
                            </label>

                            <label className={`visibility-card ${visibility === 'internal' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="internal"
                                    checked={visibility === 'internal'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M2 5.5a3.5 3.5 0 1 1 5.898 2.549 5.508 5.508 0 0 1 3.034 4.084.75.75 0 1 1-1.482.235 4 4 0 0 0-7.9 0 .75.75 0 0 1-1.482-.236A5.507 5.507 0 0 1 3.102 8.05 3.493 3.493 0 0 1 2 5.5Zm11 .5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm2.5-1a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm.615 6.707a1 1 0 0 1-.253 1.31A1 1 0 0 1 14 12.4a3.5 3.5 0 0 0-3.5-3.5.75.75 0 0 1 0-1.5 5 5 0 0 1 5 5h-.085a.75.75 0 0 1-.665.41.748.748 0 0 1-.635-.603Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>内部</strong>
                                    <span>任何登录用户都可以查看群组和内部项目</span>
                                </div>
                            </label>

                            <label className={`visibility-card ${visibility === 'public' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="public"
                                    checked={visibility === 'public'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Zm7-3.25v2.992l2.028.812a.75.75 0 0 1-.557 1.392l-2.5-1A.751.751 0 0 1 7 8.25v-3.5a.75.75 0 0 1 1.5 0Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>公开</strong>
                                    <span>任何人都可以查看群组和公开项目</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            取消
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!name || !path || submitting}
                        >
                            {submitting ? '创建中...' : '创建群组'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default GroupListPage;
